import { get } from "lodash";
import moment from "moment";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { SelectionOption } from "../components/Form/FormSelection";
import { unParseImageUrl } from "./s3upload";
import html2canvas from "html2canvas";
import { FieldValues, SubmitErrorHandler, SubmitHandler, UseFormHandleSubmit } from "react-hook-form";

export type Order = "asc" | "desc";

export function stableSort<T>(array: T[], order: Order, orderBy: string) {
  return array.sort((a, b) => {
    return order === "desc" ? descending(a, b, orderBy) : -descending(a, b, orderBy);
  });
}

function descending(a: any, b: any, orderBy: string): number {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function randomStr(e: number = 32) {
  var t = "ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678",
    a = t.length,
    n = "";
  for (let i = 0; i < e; i++) n += t.charAt(Math.floor(Math.random() * a));
  return n;
}

export function sleep(time = 300) {
  return new Promise((resolve) => setTimeout(resolve, time));
}

export function filterOnKeys(obj: any, keys: string[] = [], value = "") {
  const v = value.toLowerCase();
  return keys.some((k) => {
    let kv = "" as any;
    if (k.indexOf(".") >= 0) {
      kv = obj;
      k.split(".").forEach((j) => {
        kv = kv[j] || "";
      });
    } else {
      kv = obj[k] || "";
    }
    return (kv + "").toLowerCase().indexOf(v) >= 0;
  });
}

export function titleStr(str: string | undefined, upper?: boolean) {
  if (!str) return "";
  return str
    .replace(/(_|-)/g, " ")
    .trim()
    .replace(/\w\S*/g, function (str) {
      return upper ? str.toUpperCase() : str.charAt(0).toUpperCase() + str.substr(1);
    })
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");
}

export function enumToArraySelection(param: any): SelectionOption[] {
  return Object.entries(param).map(([key, value]) => ({
    label: titleStr(key),
    value: value as string | number | boolean,
  }));
}

export function randomInteger(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function parseQueryString(query = "") {
  if (!query) {
    return undefined;
  }
  var vars = query.split("&");
  var query_string = {} as any;
  for (var i = 0; i < vars.length; i++) {
    var pair = vars[i].split("=");
    var key = decodeURIComponent(pair[0]);
    var value = decodeURIComponent(pair[1]);
    // If first entry with this name
    if (typeof query_string[key] === "undefined") {
      query_string[key] = decodeURIComponent(value);
      // If second entry with this name
    } else if (typeof query_string[key] === "string") {
      var arr = [query_string[key], decodeURIComponent(value)];
      query_string[key] = arr;
      // If third or later entry with this name
    } else {
      query_string[key].push(decodeURIComponent(value));
    }
  }
  return query_string;
}

export function getImageWidthHeightByFile(file: File): Promise<{ width: number; height: number }> {
  const objectUrl = window.URL.createObjectURL(file);
  const img = new Image();
  img.src = objectUrl;
  return new Promise((resolve, reject) => {
    img.onload = function () {
      window.URL.revokeObjectURL(objectUrl);
      if (img.width && img.height) {
        resolve({
          width: img.width,
          height: img.height,
        });
      } else {
        reject(`cannot image get width and height by file ${file.name}`);
      }
    };
  });
}

export function getHHMMSSBySeconds(secs: number) {
  return moment.utc(secs * 1000).format("HH:mm:ss");
}

export enum ImageSize {
  thumbnail = "/fit-in/640x360",
  normal = "/fit-in/1024x576",
  none = "",
}

export function parseImageUrl(url?: string, size: ImageSize = ImageSize.thumbnail) {
  if (!url) {
    return url;
  }
  if (url.indexOf("blob") === 0) {
    return url;
  }
  url = unParseImageUrl(url);
  const u = new URL(url, process.env.REACT_APP_IMAGE_PREFIX);
  return process.env.REACT_APP_IMAGE_PREFIX + size + u.pathname;
}

export function parseAssetUrl(url?: string) {
  if (!url) {
    return url;
  }
  if (url.indexOf("blob") === 0) {
    return url;
  }
  url = unParseImageUrl(url);
  const u = new URL(url, process.env.REACT_APP_IMAGE_PREFIX);
  return process.env.REACT_APP_IMAGE_PREFIX + u.pathname;
}

export function getValueFromName<T>(obj: any, name: string): T | undefined {
  if (!obj) {
    return;
  }
  try {
    const keys = name.split(".");
    let v: any = { ...obj };
    while (keys.length > 0) {
      const k = keys.shift() as string;
      v = v[k];
    }
    return v;
  } catch (error) {
    console.error("get value error", error);
  }
}

export function getFormErrorMessage(obj: any): string {
  if (!obj) {
    return "";
  }
  const str = keyify({ ...obj }) as string[];
  const messages = str.filter((s) => s.indexOf(".message") >= 0).map((k) => `${k.replace(".message", "")} has error "${get(obj, k)}"`);
  return messages.join(", ");
}

const keyify = (obj: any, prefix = ""): any =>
  Object.keys(obj).reduce((res: any, el: any) => {
    if (typeof obj[el] === "object" && obj[el]) {
      return [...res, ...keyify(obj[el], prefix + el + ".")];
    } else if (Array.isArray(obj[el])) {
      return res;
    }
    return [...res, prefix + el];
  }, []);

export function loadJS(FILE_URL: string, async = true) {
  return new Promise((resolve, reject) => {
    if (Boolean(document.querySelector('script[src="' + FILE_URL + '"]'))) {
      resolve(true);
      return;
    }
    let scriptEle = document.createElement("script");

    scriptEle.setAttribute("src", FILE_URL);
    scriptEle.setAttribute("type", "text/javascript");
    scriptEle.setAttribute("async", `${async}`);

    document.body.appendChild(scriptEle);

    // success event
    scriptEle.addEventListener("load", () => {
      resolve(true);
    });
    // error event
    scriptEle.addEventListener("error", (ev) => {
      reject(ev);
    });
  });
}

export function getBlobUrl(url: string) {
  return new Promise((resolve, reject) => {
    var xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.responseType = "blob";

    xhr.onload = function (e) {
      if (this.status === 200) {
        var blob = new Blob([this.response], { type: "application/pdf" });
        resolve(URL.createObjectURL(blob));
      } else {
        reject(e);
      }
    };
    xhr.send();
  });
}

export function offsetToString(offset: number) {
  // Determine the sign of the offset
  var sign = offset >= 0 ? "+" : "-";

  // Get the absolute value of the offset
  var absOffset = Math.abs(offset);

  // Calculate hours and minutes
  var hours = Math.floor(absOffset);
  var minutes = Math.round((absOffset - hours) * 60);

  // Format hours and minutes with leading zeros if needed
  var formattedHours = ("0" + hours).slice(-2);
  var formattedMinutes = ("0" + minutes).slice(-2);

  // Construct the string representation
  return sign + formattedHours + ":" + formattedMinutes;
}

export function getDeviceInfo() {
  const userAgent = navigator.userAgent;
  const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isOnline = navigator.onLine;

  let deviceInfo = {
    userAgent: userAgent,
    isTouch: isTouch,
    isOnline: isOnline,
    hardwareConcurrency: navigator.hardwareConcurrency,
    platform: navigator.platform,
    vendor: navigator.vendor,
    language: navigator.language,
    devicePixelRatio: window.devicePixelRatio,
  };

  return deviceInfo;
}

export function generateDeviceId() {
  const userAgent = navigator.userAgent;
  const timezoneOffset = new Date().getTimezoneOffset();
  const language = navigator.language || (navigator as any).userLanguage;
  const deviceMemory = (navigator as any).deviceMemory || -1;
  const hardwareConcurrency = navigator.hardwareConcurrency || -1;

  const deviceInfo = `${userAgent}${timezoneOffset}${language}${deviceMemory}${hardwareConcurrency}`;

  let deviceId = "";
  for (let i = 0; i < deviceInfo.length; i++) {
    deviceId += deviceInfo.charCodeAt(i).toString(16);
  }

  return deviceId;
}

export function downloadFile(filename: string, blobObj: Blob | MediaSource) {
  const link = document.createElement("a");
  const url = URL.createObjectURL(blobObj);
  link.style.display = "none";
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function downloadScreenshot(props?: { fileName?: string; target?: HTMLElement; errorMessage?: string }) {
  const { target = document.body, fileName = "screenshot", errorMessage } = props || {};
  html2canvas(target)
    .then((canvas) => {
      const image = canvas.toDataURL();
      const link = document.createElement("a");
      link.download = `${fileName}.png`;
      link.href = image;
      link.click();
    })
    .catch((error) => {
      if (errorMessage) {
        window.webApp.showMessage("error", errorMessage);
      }
      console.error(error);
    });
}

export function downloadCsv(name: string, content: string | string[][]) {
  if (typeof content !== "string") {
    content = content
      .map((row) =>
        row
          .map((str) => {
            if (!str) return "";
            if (/[",\n\r]/.test(str)) {
              return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
          })
          .join(",")
      )
      .join("\n");
  }
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  downloadFile(name, blob);
}

export async function getDeviceId() {
  try {
    const fp = await FingerprintJS.load();
    return (await fp.get()).visitorId;
  } catch (error) {
    console.error(error);
    return generateDeviceId();
  }
}

export function findNestedErrorMessage(obj: any): string | null {
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const message = findNestedErrorMessage(item);
      if (message) {
        return message;
      }
    }
  }
  if (obj && typeof obj === "object") {
    if ("message" in obj) {
      return obj.message;
    }
    for (const key in obj) {
      const message = findNestedErrorMessage(obj[key]);
      if (message) {
        return message;
      }
    }
  }
  return null;
}

export function isDisableFutureQuestion(key: string) {
  key = key.toLowerCase();
  return key == "date_time" || key.includes("disable_future");
}

export function isFilterQuestion(key: string) {
  key = key.toLowerCase();
  return key.startsWith("filter_");
}

export function isTextInputQuestion(key: string) {
  key = key.toLowerCase();
  const whiteList: string[] = ["PromoOffer_textbox", "Top_textbox_1", "Improvement_openend_textbox", "Open_end_Text_1", "Why_Not_SAT"].map(
    (item) => item.toLowerCase()
  );
  return key.includes("sentiment") || whiteList.includes(key);
}

export const complainReasonPrefix = "complain_reason_";

export const complainReasonMap = {
  [`${complainReasonPrefix}app`]: "App or website not working in-store",
  [`${complainReasonPrefix}bill`]: "Bill errors, poor card machine service, or lack of payment options",
  [`${complainReasonPrefix}cluttered`]: "Cluttered, dirty, or disorganised layout",
  [`${complainReasonPrefix}complicated`]: "Complicated return policies",
  [`${complainReasonPrefix}confusing`]: "Confusing loyalty point system or lack of rewards",
  [`${complainReasonPrefix}customer`]: "Customer service",
  [`${complainReasonPrefix}delays`]: "Delays in refunds or exchanges",
  [`${complainReasonPrefix}dietary`]: "Dietary requirements ignored",
  [`${complainReasonPrefix}expired`]: "Expired, damaged, or poor-quality products",
  [`${complainReasonPrefix}faulty`]: "Faulty equipment",
  [`${complainReasonPrefix}faulty2`]: "Faulty self-checkouts or card machines",
  [`${complainReasonPrefix}food`]: "Food not fresh, cold, or poorly presented",
  [`${complainReasonPrefix}hard`]: "Hard to find assistance",
  [`${complainReasonPrefix}incorrect`]: "Incorrect pricing or misleading promotions",
  [`${complainReasonPrefix}limited`]: "Limited fresh produce",
  [`${complainReasonPrefix}limited2`]: "Limited parking or inaccessible entrances",
  [`${complainReasonPrefix}location`]: "Location too busy",
  [`${complainReasonPrefix}long`]: "Long wait times (general)",
  [`${complainReasonPrefix}long2`]: "Long wait times (checkout)",
  [`${complainReasonPrefix}loud`]: "Loud environment, poor lighting, or uncomfortable seating",
  [`${complainReasonPrefix}loyalty`]: "Loyalty app or card not working at checkout",
  [`${complainReasonPrefix}out`]: "Out-of-stock items",
  [`${complainReasonPrefix}pest`]: "Pest sightings (flies, rodents, etc.)",
  [`${complainReasonPrefix}poor`]: "Poor product range or missing items",
  [`${complainReasonPrefix}shelves`]: "Shelves not stocked or popular items missing",
  [`${complainReasonPrefix}staff`]: "Staff not helpful, inattentive, or rude",
  [`${complainReasonPrefix}unclean`]: "Unclean facilities",
  [`${complainReasonPrefix}wrong`]: "Wrong or missing items",
  [`${complainReasonPrefix}other`]: "OTHER",
};

export async function onHandleFormSubmit<T extends FieldValues>(handleSubmit: UseFormHandleSubmit<T>, format?: (data: T) => Promise<T>) {
  return new Promise<T>((resolve, reject) => {
    handleSubmit(
      async (data) => {
        try {
          if (format) {
            data = await format(data);
          }
          resolve(data);
        } catch (error) {
          window.webApp.showMessage("error", "There is something wrong with the form, please fill in correct information");
          reject(error);
        }
      },
      (errors) => {
        window.webApp.showMessage("error", "There is something wrong with the form, please fill in correct information");
        reject(errors);
      }
    )();
  });
}
